import { useRef, useState, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import moment from 'moment';
import { Button, Loading, Tooltip } from 'react-daisyui';
import { LightBulbIcon } from '@heroicons/react/24/outline';
import { useChatContext } from '../../../../../../Chat.hook';
import { useMessageBarContext } from '../../../../MessageBar.hook';
import { useModalsContext } from '../../../../../../../../providers/ModalsProvider/Modals.context';
import { suggestionsService } from '../../../../../../../../services/AI/suggestions.service';
import { tChatEvent } from '../../../../../../Chat.types';
import { useAnalytics } from 'src/hooks/analytics/useAnalytics.hook';
import SuggestedResponsesSelector from './components/SuggestedResponsesSelector/SuggestedResponsesSelector.component';
import { getCutOffTimeByHours } from 'src/utils/messageFilters';

export default function SuggestedResponsesAction(props: { tooltipMessage: string }) {
  const { selectedChat, chatEvents } = useChatContext();
  const { setMessageBarValue, setLastSuggestedResponse } =
    useMessageBarContext();
  const { selectedLeadForChatModal } = useModalsContext();
  const [isLoading, setIsLoading] = useState(false);
  const [responses, setResponses] = useState<{ text: string; tone: string }[]>(
    [],
  );
  const [isOpen, setIsOpen] = useState(false);
  const [aiSuggestedError, setAiSuggestedError] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const portalRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [menuPos, setMenuPos] = useState({ bottom: 0, left: 0 });

  const { trackEvent } = useAnalytics();

  // Cerrar dropdown al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        dropdownRef.current && !dropdownRef.current.contains(target) &&
        (!portalRef.current || !portalRef.current.contains(target))
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useLayoutEffect(() => {
    if (isOpen && triggerRef.current) {
      const menuWidth = 320; // w-80
      const rect = triggerRef.current.getBoundingClientRect();
      const rawLeft = rect.left + rect.width / 2 - menuWidth / 2;
      setMenuPos({
        bottom: window.innerHeight - rect.top + 8,
        left: Math.max(8, Math.min(rawLeft, window.innerWidth - menuWidth - 8)),
      });
    }
  }, [isOpen]);

  // Función para convertir los mensajes del chat al formato requerido
  function convertChatEventsToHistory(chatEvents: tChatEvent[], cutOffTime: moment.Moment) {
    return chatEvents
      .filter((event) => {
        const eventTime = moment(event.createdAt);
        return eventTime.isSameOrAfter(cutOffTime) && event.content?.text && event.type === 'MESSAGE'
      }) // Solo mensajes con texto
      .map((event) => {
        return {
          role: (event.message?.fromMe ? 'assistant' : 'user') as 'assistant' | 'user',
          content: event.content?.text || '',
          timestamp: event.createdAt,
        };
      })
      .sort((a, b) => moment(b.timestamp).diff(moment(a.timestamp)))
      .slice(0, 15);
  }

  // Función para generar una respuesta sugerida
  async function generateSuggestedResponse() {

    const chatId = selectedChat?.id || "";
    setAiSuggestedError(null);

    if (!chatEvents || !chatEvents.length) {
      setAiSuggestedError('Necesitas mensajes recientes de tu lead (últimos 7 días) para generar sugerencias de respuesta');

      return
    }

    // Al hacer clic en generar
    trackEvent('front.chat.ai.suggested_response.generate.click', {
      conversationId: chatId,
      messageCount: chatEvents.length,
    });

    setIsLoading(true);

    try {
      setResponses([]);
      const cutOffTime = getCutOffTimeByHours(168);
      const conversationHistory = convertChatEventsToHistory(chatEvents, cutOffTime);

      // Solo generar sugerencias si hay al menos un mensaje del usuario
      const hasUserMessage = conversationHistory.some(
        (msg) => msg.role === 'user',
      );
      if (!conversationHistory.length || !hasUserMessage) {
        setIsLoading(false);
        setAiSuggestedError('Las sugerencias de respuesta solo están disponibles si tu lead ha enviado mensajes en los últimos 7 días.');
        return;
      }

      const leadContext = {
        stage: selectedChat?.lead?.stages?.[0]?.name,
        status: selectedChat?.lead?.statuses?.[0]?.name,
        tags: selectedLeadForChatModal?.tags || [],
      };

      const conversationContext = conversationHistory
        .map(
          (msg) =>
            `${msg.role === 'user' ? 'Cliente' : 'Agente'}: ${msg.content}`,
        )
        .join('\n');

      const newResponses = await suggestionsService.generateSuggestions(chatId, {
        conversation: conversationContext,
        leadContext,
      });

      setResponses(newResponses);

      if (newResponses.length === 0) {
        setAiSuggestedError('No se pudieron generar sugerencias.');
      }

      // Al generar exitosamente
      trackEvent('front.chat.ai.suggested_response.generate.success', {
        conversationId: selectedChat?.id,
        responsesCount: newResponses.length,
      });
    } catch (error) {
      console.error('Error generating suggestion:', error);

      setAiSuggestedError('No se pudieron generar sugerencias.');
    } finally {
      setIsLoading(false);
    }
  }

  function handleToggleDropdown(e: React.MouseEvent) {
    e.preventDefault();
    const nextState = !isOpen;
    setIsOpen(nextState);

    if (nextState) {
      generateSuggestedResponse();
    }
  }

  function handleSelectResponse(
    response: { text: string; tone: string },
    index: number,
  ) {
    trackEvent('front.chat.ai.suggested_response.selected', {
      conversationId: selectedChat?.id,
      responseIndex: index,
      tone: response.tone,
      wasModified: false,
    });

    setMessageBarValue(response.text);
    setLastSuggestedResponse(response);
    setIsOpen(false);
  }

  if (!selectedChat) return null;

  return (
    <div ref={dropdownRef} className="relative">
      <Tooltip message={props.tooltipMessage}>
        <Button
          color="ghost"
          onClick={handleToggleDropdown}
          size="xs"
          className="MessageBar__Button"
          disabled={isLoading}
          ref={triggerRef}
        >
          {isLoading ? (
            <Loading size="xs" />
          ) : (
            <LightBulbIcon className="MessageBar__Icon" />
          )}
        </Button>
      </Tooltip>
      {isOpen && createPortal(
        <div
          ref={portalRef}
          className="fixed z-[9999]"
          style={{ bottom: menuPos.bottom, left: menuPos.left }}
        >
          <SuggestedResponsesSelector
            responses={responses}
            onSelect={handleSelectResponse}
            onRegenerate={generateSuggestedResponse}
            isLoading={isLoading}
            aiSuggestedError={aiSuggestedError}
          />
        </div>,
        document.body,
      )}
    </div>
  );
}
